import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Smartphone } from 'lucide-react';
import type { PaymentMethod } from '@/domain/model/PaymentMethod';
import type { PaymentProviderId } from '@/domain/model/PaymentProviderId';

interface PaymentMethodSelectorProps {
  availableMethods: PaymentMethod[];
  selectedProvider: PaymentProviderId | null;
  onSelect: (provider: PaymentProviderId) => void;
  isLoading?: boolean;
}

const providerIcons: Record<PaymentProviderId, React.ReactNode> = {
  stripe: <CreditCard className="w-6 h-6" />,
  paypal: <CreditCard className="w-6 h-6" />,
  wave: <Smartphone className="w-6 h-6" />,
};

export const PaymentMethodSelector = ({
  availableMethods,
  selectedProvider,
  onSelect,
  isLoading = false,
}: PaymentMethodSelectorProps) => {
  const { t } = useTranslation();

  const sortedMethods = useMemo(
    () => availableMethods.filter((m) => m.enabled).sort((a, b) => a.sortOrder - b.sortOrder),
    [availableMethods],
  );

  if (sortedMethods.length === 0) {
    return (
      <div className="text-center py-8">
        <CreditCard className="w-12 h-12 mx-auto text-[var(--color-ink-faint)] mb-4" />
        <p className="text-[var(--color-ink-soft)]">{t('payment.noMethodsAvailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-[var(--color-ink)]">
        {t('payment.selectPaymentMethod')}
      </h3>

      <div className="space-y-3">
        {sortedMethods.map((method) => {
          const isSelected = selectedProvider === method.provider;

          return (
            <button
              key={method.provider}
              type="button"
              className={`relative w-full flex items-center p-4 border-2 rounded-lg transition-all duration-200 text-left ${
                isSelected
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent-tint)]'
                  : 'border-[var(--color-line)] bg-[var(--color-surface)] hover:border-[var(--color-line-strong)]'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => !isLoading && onSelect(method.provider)}
              disabled={isLoading}
            >
              <div className="flex items-center gap-4">
                <span
                  className={
                    isSelected ? 'text-[var(--color-accent)]' : 'text-[var(--color-ink-soft)]'
                  }
                >
                  {providerIcons[method.provider]}
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-[var(--color-ink)]">{method.displayName}</h4>
                  <p className="text-sm text-[var(--color-ink-faint)] mt-0.5">
                    {method.description}
                  </p>
                </div>
              </div>

              <div className="absolute top-4 right-4">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)]'
                      : 'border-[var(--color-line-strong)] bg-[var(--color-surface)]'
                  }`}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-[var(--color-on-accent)]" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
